import { HttpResponse, HttpStatusEnum } from '@core/api';
import { AlertSeverityTypeEnum } from '@shared/enums';
import * as moment from 'moment';
import { BannerType } from '../enums/banner-type.enum';
import {
    DashboardData,
    DashboardDataDTO,
    PrepareScheduleCourierPickupData,
    PrepareScheduleCourierPickupDTO,
    ScheduleCourierPickupData,
    ScheduleCourierPickupDTO,
} from '../interfaces';

export class DashboardMapper {
    public static mapBannerTypeToAlertSeverityTypeEnum(bannerType: BannerType): AlertSeverityTypeEnum {
        switch (bannerType) {
            case BannerType.INFO:
                return AlertSeverityTypeEnum.INFO;
            case BannerType.WARNING:
                return AlertSeverityTypeEnum.WARNING;
            case BannerType.CUSTOM:
                return AlertSeverityTypeEnum.CUSTOM;
        }
    }
    public static mapGetDashboardData(data: DashboardDataDTO): HttpResponse<DashboardData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                banner: data.banner
                    ? {
                          bannerType: DashboardMapper.mapBannerTypeToAlertSeverityTypeEnum(data.banner.bannerType),
                          canNotBeDismissed: data.banner.canNotBeDismissed,
                          message: data.banner.message,
                      }
                    : null,
                hasDefaultPaymentMethod: data.hasDefaultPaymentMethod,
                ordersData: {
                    totalNumberOfOrders: data.totalNumberOfOrders,
                    recentOrders: data.recentOrders.map(order => {
                        return {
                            id: order.id,
                            orderName: order.compositeName,
                            status: order.status,
                            requester: order.requester.firstName + ' ' + order.requester.lastName,
                            numberOfReactions: order.numberOfReactions,
                            dateCreated: order.createdDate,
                        };
                    }),
                },
                expiringSamplesData: {
                    totalNumberOfExpiringSamples: data.totalNumberOfExpiringSamples,
                    expiringSamples: data.expiringSamples.map(sample => {
                        return {
                            id: sample.id,
                            sampleName: sample.name,
                            lastUsed: sample.lastUsedDate,
                            storageDateEnd: sample.storageEndDate,
                        };
                    }),
                },
                orderResultsData: {
                    totalNumberOfOrdersAndRetestsWithResults: data.totalNumberOfOrdersAndRetestsWithResults,
                    ordersAndRetestsWithRecentResults: data.ordersAndRetestsWithRecentResults.map(order => {
                        return {
                            orderName: order.compositeName,
                            orderId: order.id,
                            numberOfReactions: order.numberOfReactions,
                            resulted: order.resultedDate,
                            displayDownload: true,
                        };
                    }),
                },
                courierPickupDetails: data.courierInformation
                    ? {
                          courierName: data.courierInformation.courier,
                          establishedCourierOrganization: data.courierInformation.establishedCourierOrganization,
                          requestCutoffTime:
                              moment(data.courierInformation.cutOffTime, ['HH:mm']).format('LT') + ' PST',
                          dropboxCutoffTime:
                              moment(data.courierInformation.pickupTime, ['HH:mm']).format('LT') + ' PST',
                      }
                    : null,
            },
        };
    }

    public static mapGetPrepareSchedulePickupData(
        data: PrepareScheduleCourierPickupDTO
    ): HttpResponse<PrepareScheduleCourierPickupData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                firstName: data.firstName,
                lastName: data.lastName,
                organization: data.organization,
                group: data.group,
                courier: data.courier,
                orders: data.orders.map(order => {
                    return {
                        id: order.id,
                        compositeName: order.compositeName,
                        numberOfTubes: order.numberOfTubes,
                    };
                }),
                pickupLocations: data.pickupLocations.map(location => {
                    return {
                        id: location.id,
                        description: location.description,
                        pickupTime: moment(location.pickupTime, ['HH']).format('LT') + ' PST',
                        cutOffTime: moment(location.cutOffTime, ['HH']).format('LT') + ' PST',
                    };
                }),
            },
        };
    }

    public static mapScheduleCourierPickupData(
        data: ScheduleCourierPickupDTO
    ): HttpResponse<ScheduleCourierPickupData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                message: data?.message ?? '',
            },
        };
    }
}
